import IndexedDBHelper from './IndexedDBHelper';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../app.js';

export default class OfflineSyncUtility {
  static init() {
    window.addEventListener('online', () => {
      console.log("🔄 Browser back online, syncing images...");
      this.syncAllOfflineImages();
    });

    if (navigator.onLine) {
      this.syncAllOfflineImages();
    }
  }

  static async syncAllOfflineImages() {
    const offlineImages = await IndexedDBHelper.getAllImages();

    for (const imgRecord of offlineImages) {
      const { id, imageBlob, metadata = {} } = imgRecord;

      try {
        if (metadata.type === "avatar" && metadata.userId) {
          await this.syncAvatarImage(id, imageBlob, metadata);
        } else if (metadata.type === "lead" && metadata.workflowId && metadata.componentId) {
          await this.syncWorkflowLeadImage(id, imageBlob, metadata);
        } else if (metadata.workflowId && metadata.taskId) {
          await this.syncWorkflowImage(id, imageBlob, metadata);
        } else {
          console.warn(`⚠️ Unknown or unsupported image context for image ID ${id}. Skipping.`);
        }
      } catch (err) {
        console.error(`❌ Failed to sync image ${id}:`, err);
      }
    }
  }

  static async syncAvatarImage(id, imageBlob, metadata) {
    const { userId } = metadata;
    const storageRef = ref(storage, `avatars/${userId}/${id}.jpg`);
    await uploadBytes(storageRef, imageBlob);
    const downloadURL = await getDownloadURL(storageRef);

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      console.warn(`⚠️ No user doc for userId ${userId}. Skipping image ${id}.`);
      return;
    }

    const userData = userSnap.data();
    await setDoc(userRef, {
      ...userData,
      avatar: {
        url: downloadURL,
        updatedAt: new Date().toISOString(),
      },
    });

    await IndexedDBHelper.deleteImage(id);
    console.log(`✅ Synced avatar image for user ${userId}`);
  }

  static async syncWorkflowLeadImage(id, imageBlob, metadata) {
    const { workflowId, componentId } = metadata;
    const storageRef = ref(storage, `leads/${workflowId}/${id}.jpg`);
    await uploadBytes(storageRef, imageBlob);
    const downloadURL = await getDownloadURL(storageRef);

    const workflowRef = doc(db, 'workflows', workflowId);
    const workflowSnap = await getDoc(workflowRef);
    if (!workflowSnap.exists()) {
      console.warn(`⚠️ Workflow doc not found for workflowId ${workflowId}`);
      return;
    }

    const workflowData = workflowSnap.data();

    // Update or set lead image metadata
    await setDoc(workflowRef, {
      ...workflowData,
      leadImages: {
        ...(workflowData.leadImages || {}),
        [componentId]: {
          url: downloadURL,
          uploadedAt: new Date().toISOString(),
        },
      },
    });

    await IndexedDBHelper.deleteImage(id);
    console.log(`✅ Synced workflow lead image for ${workflowId}`);
  }

  static async syncWorkflowImage(id, imageBlob, metadata) {
    const { workflowId, taskId, componentId } = metadata;

    const storageRef = ref(storage, `images/${workflowId}/${taskId}/${id}.jpg`);
    await uploadBytes(storageRef, imageBlob);
    const downloadURL = await getDownloadURL(storageRef);

    const workflowTasksRef = doc(db, "workflows", workflowId, "tasks", taskId);
    const taskSnap = await getDoc(workflowTasksRef);
    if (!taskSnap.exists()) {
      console.warn(`⚠️ Task not found for image ID ${id}.`);
      return;
    }

    const taskData = taskSnap.data();
    const eventIndex = taskData.events?.findIndex(
      (event) => event.component?.id === componentId
    );

    if (eventIndex === -1 || eventIndex === undefined) {
      console.warn(`⚠️ No matching event found for component ID ${componentId}.`);
      return;
    }

    const event = taskData.events[eventIndex];
    if (event.answer?.photo?.id === id) {
      event.answer.photo.url = downloadURL;
      event.answer.photo.status = "uploaded";
    } else if (Array.isArray(event.images)) {
      const imageIndex = event.images.findIndex((img) => img.id === id);
      if (imageIndex !== -1) {
        event.images[imageIndex].url = downloadURL;
        event.images[imageIndex].status = "uploaded";
      } else {
        console.warn(`⚠️ No matching image in event.images for ID ${id}`);
        return;
      }
    } else {
      console.warn(`⚠️ No valid photo or images in event for ID ${id}`);
      return;
    }

    await setDoc(workflowTasksRef, taskData);
    await IndexedDBHelper.deleteImage(id);
    console.log(`✅ Synced workflow image ${id}`);
  }
}
